import { useList, useDelete, useUpdate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  StatusBadge,
  getExpiryStatus,
} from '@/components/shared';
import {
  DataTable,
  TableToolbar,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  MousePointerClick,
  TrendingUp,
  ArrowUpDown,
  ImageIcon,
  X,
  Users,
} from 'lucide-react';
import { ReorderModal } from '@/components/modals';
import { type Announcement, getAnnouncementBackgroundUrl, calculateEngagementRate } from './types';
import { AnnouncementEventsDialog, type EventView } from './AnnouncementEventsDialog';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { notify } from '@/lib/notify';

function formatNumber(num: number): string {
  if (num >= 1000000) { return (num / 1000000).toFixed(1) + 'M'; }
  if (num >= 1000) { return (num / 1000).toFixed(1) + 'K'; }
  return num.toString();
}

export function AnnouncementsList() {
  const navigate = useNavigate();
  useScrollRestoration();
  const [page, setPage] = usePersistedState('announcements:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('announcements:sort', { field: 'order', order: 'asc' });
  const [search, setSearch] = usePersistedState('announcements:search', '');
  const [activeFilter, setActiveFilter] = usePersistedState<string>('announcements:activeFilter', 'all');
  const [deleteItems, setDeleteItems] = useState<Announcement[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [eventsDialog, setEventsDialog] = useState<{ announcement: Announcement; type: EventView } | null>(null);
  const pageSize = 20;

  const { result, query } = useList<Announcement>({
    resource: 'announcements',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: [
      ...(activeFilter !== 'all'
        ? [{ field: 'isActive', operator: 'eq' as const, value: activeFilter === 'true' }]
        : []),
      ...(search
        ? [{ field: 'search', operator: 'contains' as const, value: search }]
        : []),
    ],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { mutate: deleteAnnouncement } = useDelete();
  const { mutate: updateAnnouncement } = useUpdate();

  const announcements = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;

  const handleToggleActive = (items: Announcement[]) => {
    if (items.length === 0) { return; }
    items.forEach((announcement) => {
      updateAnnouncement({
        resource: 'announcements',
        id: announcement.id,
        values: { isActive: !announcement.isActive },
        successNotification: false,
      });
    });
    const activating = items.every((i) => !i.isActive);
    notify.success(`${activating ? 'Активировано' : 'Деактивировано'}: ${items.length}`);
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) { return; }
    const count = deleteItems.length;
    for (const item of deleteItems) {
      deleteAnnouncement({ resource: 'announcements', id: item.id, successNotification: false });
    }
    setDeleteItems([]);
    notify.success(`Удалено: ${count}`);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) { return '—'; }
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns: Column<Announcement>[] = [
    {
      key: 'images',
      header: 'Объявление',
      render: (_, announcement) => {
        const bgUrl = getAnnouncementBackgroundUrl(announcement);
        return (
          <div className="flex items-center gap-3">
            {bgUrl ? (
              <img src={bgUrl} alt={`Объявление #${announcement.id}`} className="h-12 w-24 object-cover rounded" />
            ) : (
              <div className="h-12 w-24 bg-muted rounded flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate max-w-[200px]">Объявление #{announcement.id}</p>
              <p className="text-sm text-muted-foreground">
                {!announcement.startDate && !announcement.endDate
                  ? 'Бессрочно'
                  : `${announcement.startDate ? formatDate(announcement.startDate) : '...'} — ${announcement.endDate ? formatDate(announcement.endDate) : 'Бессрочно'}`}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'imagesCount',
      header: 'Изобр.',
      width: '80px',
      hiddenOnMobile: true,
      render: (_, announcement) => <span className="text-sm text-muted-foreground">{announcement.images.length}</span>,
    },
    {
      key: 'stats',
      header: 'Статистика',
      width: '360px',
      hiddenOnMobile: true,
      render: (_, announcement) => (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Просмотры (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ announcement, type: 'impression' });
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            {formatNumber(announcement.viewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные просмотры"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ announcement, type: 'unique-impressions' });
            }}
          >
            <Users className="h-3.5 w-3.5" />
            {formatNumber(announcement.uniqueViewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Клики (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ announcement, type: 'click' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            {formatNumber(announcement.clicksCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные клики"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ announcement, type: 'unique-clicks' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            <Users className="h-3 w-3 -ml-0.5" />
            {formatNumber(announcement.uniqueClicksCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Закрытия"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ announcement, type: 'close' });
            }}
          >
            <X className="h-3.5 w-3.5" />
            {formatNumber(announcement.closesCount || 0)}
          </button>
          <span className="flex items-center gap-1" title="CTR">
            <TrendingUp className="h-3.5 w-3.5" />
            {(announcement.viewsCount > 0
              ? calculateEngagementRate(announcement.viewsCount, announcement.clicksCount)
              : 0
            ).toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      key: 'linkUrl',
      header: 'Ссылка',
      width: '80px',
      hiddenOnMobile: true,
      render: (_, announcement) =>
        announcement.linkUrl ? (
          <a href={announcement.linkUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      render: (_, announcement) => {
        if (!announcement.isActive) {
          return <StatusBadge status="disabled" customLabel="Выключено" size="sm" />;
        }
        const expiryStatus = getExpiryStatus(
          announcement.startDate ? new Date(announcement.startDate) : null,
          announcement.endDate ? new Date(announcement.endDate) : null,
        );
        if (expiryStatus === 'expired') { return <StatusBadge status="expired" size="sm" />; }
        if (expiryStatus === 'scheduled') { return <StatusBadge status="scheduled" size="sm" />; }
        return <StatusBadge status="active" size="sm" />;
      },
    },
  ];

  const bulkActions: BulkAction<Announcement>[] = [
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) { navigate(`/announcements/edit/${items[0].id}`); }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Активировать',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => i.isActive)),
      variant: 'outline',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount = activeFilter !== 'all' ? 1 : 0;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Объявления"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setReorderOpen(true)}>
              <ArrowUpDown className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Порядок</span>
            </Button>
            <Button size="sm" onClick={() => navigate('/announcements/create')}>
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Добавить объявление</span>
            </Button>
          </div>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по ID..."
        filters={
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="announcement-active">Статус активности</Label>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger id="announcement-active">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="true">Активные</SelectItem>
                  <SelectItem value="false">Неактивные</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setSearch('');
          setActiveFilter('all');
        }}
      />

      <DataTable
        columns={columns}
        data={announcements}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(announcement) => navigate(`/announcements/edit/${announcement.id}`)}
        emptyMessage="Объявления не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1 ? 'Удалить объявление?' : `Удалить ${deleteItems.length} объявлений?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>Вы уверены, что хотите удалить объявление #{deleteItems[0].id}? Это действие нельзя отменить.</>
              ) : (
                <>Вы уверены, что хотите удалить {deleteItems.length} объявлений? Это действие нельзя отменить.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnnouncementEventsDialog
        announcement={eventsDialog?.announcement ?? null}
        type={eventsDialog?.type ?? null}
        open={eventsDialog !== null}
        onOpenChange={(open) => {
          if (!open) { setEventsDialog(null); }
        }}
      />

      <ReorderModal
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        resource="announcements"
        items={announcements}
        title="Порядок объявлений"
        renderItem={(announcement) => {
          const bgUrl = getAnnouncementBackgroundUrl(announcement);
          return (
            <div className="flex items-center gap-3">
              {bgUrl ? (
                <img src={bgUrl} alt="" className="h-10 w-16 object-cover rounded" />
              ) : (
                <div className="h-10 w-16 bg-muted rounded flex items-center justify-center">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">Объявление #{announcement.id}</p>
                <p className="text-xs text-muted-foreground">{announcement.images.length} изобр.</p>
              </div>
            </div>
          );
        }}
        onSuccess={() => query.refetch()}
      />
    </div>
  );
}
